// Kept in sync with functions/utils/sentryHelpers.js -- same fix, same
// reasoning: matched case/separator-insensitively (see normalizeKey below)
// so header-name variants of an already-listed concept don't need their own
// entry, but a genuinely different key (like "x-api-key" vs "apiKey") still
// needs its own listing. See that file's comment for the full incident
// context (a real credential-exposure gap found via Codex review on #1958).
const SENSITIVE_KEYS = [
  "password",
  "newPassword",
  "currentPassword",
  "passwordHash",
  "passwordSalt",
  "token",
  "accessToken",
  "refreshToken",
  "idToken",
  "access_token",
  "refresh_token",
  "secret",
  "clientSecret",
  "client_secret",
  "apiKey",
  "privateKey",
  "authorization",
  "x-api-key",
  "x-auth-token",
  "x-admin-api-key",
  "cookie",
  "set-cookie",
  "x-csrf-token"
];

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

const SENSITIVE_KEY_SET = new Set(SENSITIVE_KEYS.map(normalizeKey));

const isSensitiveKey = (key) => SENSITIVE_KEY_SET.has(normalizeKey(key));

const REDACTED = "[REDACTED]";

// A sensitive value doesn't only show up as a dict value under a sensitive
// key -- it also shows up embedded in a URL's own query string, e.g. a
// Firebase Storage download URL's persistent `token` param (unlike a
// time-limited signed URL, that token alone grants standing access to the
// object, so it's a credential in its own right). Sentry's browser SDK
// auto-records a breadcrumb with the full request URL for every fetch/XHR
// it instruments, so any Storage download URL fetched while a component is
// mounted (e.g. an <img>/<Document> source, or domToPng inlining an image)
// rides along on the next captured event's breadcrumbs regardless of what
// that event's own message/extra contain. isSensitiveKey alone can't catch
// this, since the key holding the URL (e.g. "url") isn't itself sensitive.
//
// Covers the major signed-URL schemes, not just Firebase/GCS/S3 signatures:
// X-Amz-Security-Token is a literal temporary AWS STS credential (more
// sensitive than the signature itself), X-Amz-Credential/X-Goog-Credential
// embed an access key id, and `sig` is Azure SAS's signature param.
const SENSITIVE_URL_PARAM_NAMES = [
  "token",
  "signature",
  "x-goog-signature",
  "x-amz-signature",
  "x-amz-security-token",
  "x-amz-credential",
  "x-goog-credential",
  "sig"
];

const SENSITIVE_URL_PARAM_SET = new Set(SENSITIVE_URL_PARAM_NAMES.map(normalizeKey));

// Fallback for when `new URL()` can't parse the string (malformed URL,
// unusual scheme) -- a parse failure must not mean "ship it unredacted".
// This regex can't distinguish a real query param from a path segment that
// happens to look like one, so it's intentionally broader/cruder than the
// URL-based pass above; that's the right tradeoff for a fail-closed net.
const SENSITIVE_URL_PARAM_REGEX = new RegExp(
  `([?&](?:${SENSITIVE_URL_PARAM_NAMES.join("|")})=)[^&]+`,
  "gi"
);

const scrubUrlString = (value) => {
  if (typeof value !== "string" || !/^https?:\/\//i.test(value)) {
    return value;
  }

  try {
    const url = new URL(value);
    const hadSensitiveParam = [...url.searchParams.keys()].some((key) => SENSITIVE_URL_PARAM_SET.has(normalizeKey(key)));
    if (!hadSensitiveParam) {
      return value;
    }

    [...url.searchParams.keys()]
      .filter((key) => SENSITIVE_URL_PARAM_SET.has(normalizeKey(key)))
      .forEach((key) => url.searchParams.set(key, REDACTED));
    return url.toString();
  } catch {
    return value.replace(SENSITIVE_URL_PARAM_REGEX, `$1${REDACTED}`);
  }
};

const scrubObject = (value) => {
  if (Array.isArray(value)) {
    return value.map(scrubObject);
  }

  if (typeof value === "string") {
    return scrubUrlString(value);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const entries = Object.entries(value).map(([key, val]) => {
    if (isSensitiveKey(key)) {
      return [key, REDACTED];
    }
    return [key, scrubObject(val)];
  });
  return Object.fromEntries(entries);
};

export const sentryBeforeSend = (event) => {
  if (!event) {
    return event;
  }

  return {
    ...event,
    ...(event.request ? { request: scrubObject(event.request) } : {}),
    ...(event.extra ? { extra: scrubObject(event.extra) } : {}),
    ...(event.contexts ? { contexts: scrubObject(event.contexts) } : {}),
    ...(event.breadcrumbs ? { breadcrumbs: scrubObject(event.breadcrumbs) } : {})
  };
};

// beforeSend's breadcrumb scrub above only reaches breadcrumbs attached to a
// captured error event -- it does nothing for Session Replay, which records
// fetch/XHR network activity continuously, independent of whether an error
// ever fires. Replay's own network-capture code reads the breadcrumb's `url`
// straight off the same object the core SDK's `beforeBreadcrumb` option
// produces (confirmed against the installed @sentry/replay: the client calls
// `beforeBreadcrumb` first, then emits `beforeAddBreadcrumb` for Replay's
// listener to enrich/capture -- see hub.js's addBreadcrumb), so scrubbing it
// here reaches Replay too, not just beforeSend's error-only path.
export const sentryBeforeBreadcrumb = (breadcrumb) => {
  if (!breadcrumb) {
    return breadcrumb;
  }

  return {
    ...breadcrumb,
    ...(breadcrumb.data ? { data: scrubObject(breadcrumb.data) } : {})
  };
};

// Chrome that never renders customer data, so replays stay diagnosable with
// maskAllText on: we keep "where was the user" while every data value stays
// masked. Body table cells are deliberately absent — that's where vendor
// names, spend and emissions figures live. Alert/toast text is also excluded:
// error strings interpolate customer data ("Failed to load emissions for
// Acme Corp"), and knowing an alert fired is enough without its contents.
export const REPLAY_UNMASKED_SELECTORS = [
  ".MuiDialogTitle-root",
  ".MuiTab-root",
  ".MuiTableCell-head",
  ".MuiButton-root",
  "[data-sentry-unmask]"
];

// Unauthenticated flows where the person filling the form is a third party —
// a customer's employee, an event attendee, an individual consumer — not the
// account holder. Replay is off entirely on these, not merely masked, because
// our Sentry vendor record covers error telemetry rather than screen
// recordings.
export const REPLAY_EXCLUDED_PATH_PREFIXES = ["/employee-survey", "/ev"];

export const shouldRecordReplay = (pathname) =>
  !REPLAY_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export { scrubObject, SENSITIVE_KEYS };
